<script>
  import { v4 as uuidv4 } from "uuid";
  import { useQueryClient } from "@sveltestack/svelte-query";
  import request from "superagent";
  import { _ } from "svelte-i18n";

  import HealthStatus from "./HealthStatus.svelte";
  import Sleep from "./Sleep.svelte";
  import Readiness from "./Readiness.svelte";
  import Comment from "./Comment.svelte";
  import MenstrualPain from "./MenstrualPain.svelte";

  export let date = new Date();
  export let days = [];
  export let gender = "female";

  const queryClient = useQueryClient();

  let saved = false;
  const emptyValue = (date) => ({
    date: date.toISOString().slice(0, 10),
    health: {
      resting_pulse: "",
      competitionDay: false,
      dayOff: false,
      height: false,
      travelDay: false,
      menstrualCycle: false,
      injured: false,
      ill: false,
    },
    sleep: {
      length: "",
      quality: null,
    },
    readiness: {
      mental: null,
      physical: null,
    },
    menstrualPain: null,
    comments: "",
  });
  let parameters = emptyValue(date);
  let storedValues = "";

  function convertTemporaryFormat(savedValues) {
    return Array.isArray(savedValues.health.status)
      ? savedValues.health.status.reduce(
          (health, item) => ({
            ...health,
            [item.slice("status-".length)]: true,
          }),
          {
            resting_pulse: savedValues.health.resting_pulse,
          }
        )
      : {
          resting_pulse: savedValues.health.resting_pulse,
          [savedValues.health.status.slice("status-".length)]: true,
        };
  }

  function updateValues(days, newDate) {
    let date = newDate !== undefined ? new Date(newDate) : date;
    let savedValues =
      days !== undefined
        ? days
            .slice()
            .reverse()
            .filter((item) => item !== undefined)
            .find(
              (item) =>
                item.date === date.toISOString().slice(0, 10) ||
                item.date ===
                  date.toLocaleString("no", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
            )
        : undefined;

    let parameters = {};
    if (savedValues !== undefined) {
      parameters = {
        ...savedValues,
        health:
          savedValues.health.status !== undefined
            ? convertTemporaryFormat(savedValues)
            : savedValues.health,
      };
    } else {
      parameters = emptyValue(date);
    }
    return parameters;
  }

  function saveToServer(userContent, parameters) {
    const user = userContent !== null ? JSON.parse(userContent).id : undefined;
    if (user !== undefined) {
      console.log("Save day parameters for user ", user);
      request
        .post("/api/days")
        .set("Content-Type", "application/json")
        .set("Authorization", "Bearer " + user)
        .send(parameters)
        .then((response) => {
          console.log("[INFO] Saved to server", response.body);
          let storedInLocalStorage = localStorage.getItem("days");
          let parsedContent =
            storedInLocalStorage !== null
              ? JSON.parse(storedInLocalStorage)
              : [];
          days = localStorage.setItem(
            "days",
            JSON.stringify(
              parsedContent.map((item) =>
                item.reference === response.body.reference &&
                item.id === response.body.id
                  ? { ...item, version: response.body.version }
                  : item
              )
            )
          );
          queryClient.invalidateQueries("days");
          storedValues = JSON.stringify({
            ...parameters,
            version: response.body.version,
          });
          parameters.version = response.body.version;
        })
        .catch((error) =>
          console.log("[ERROR] Not able to save to server, got", error)
        );
    }
  }

  function save(dayParameters) {
    const newParameters = {
      ...dayParameters,
      id: dayParameters.id !== undefined ? dayParameters.id : uuidv4(),
      reference: uuidv4(),
    };
    saved = true;
    let storedDays = localStorage.getItem("days");
    let days = storedDays !== null ? JSON.parse(storedDays) : [];
    localStorage.setItem("days", JSON.stringify(days.concat(newParameters)));
    parameters = newParameters;
    storedValues = JSON.stringify(parameters);

    saveToServer(localStorage.getItem("user"), newParameters);
  }

  $: {
    let savedParameters = updateValues(days, date);
    if (
      (parameters.id === undefined && savedParameters.id !== undefined) ||
      parameters.version !== savedParameters.version ||
      parameters.date !== date.toISOString().slice(0, 10)
    ) {
      parameters = savedParameters;
      storedValues = JSON.stringify(parameters);
      console.log("Updated");
    }
  }
</script>

<div class="layout__item">
  <h2 class="layout__title layout__title--inline">
    {$_("dayParameters.title")}
  </h2>
  <div class="card">
    <section class="card__section card__section--float-date-picker-left">
      <div class="DayPicker-wrapper DayPicker-wrapper--calendar-icon">
        <label
          for="74ae0061-2f1f-4f62-8043-24bdb51102ba"
          class="input DayPicker__input"
        >
          <input
            type="date"
            value={date.toISOString().slice(0, 10)}
            on:change={(event) => (date = new Date(event.target.value))}
            class="input__text input__text--size-false"
            id="74ae0061-2f1f-4f62-8043-24bdb51102ba"
            mask="11.11.1111"
            maxlength="10"
            size="10"
          />
        </label>
      </div>
      <div>
        {#if JSON.stringify(parameters) !== storedValues && JSON.stringify(parameters) !== JSON.stringify(emptyValue(date))}
          <div class="info-text">{$_("dayParameters.saveReminder")}</div>
        {/if}
        <HealthStatus
          bind:ill={parameters.health.ill}
          bind:injured={parameters.health.injured}
          bind:menstrualCycle={parameters.health.menstrualCycle}
          bind:travelDay={parameters.health.travelDay}
          bind:height={parameters.health.height}
          bind:dayOff={parameters.health.dayOff}
          bind:competitionDay={parameters.health.competitionDay}
          bind:pulse={parameters.health.resting_pulse}
          bind:gender
        />
        <Sleep
          bind:quality={parameters.sleep.quality}
          bind:length={parameters.sleep.length}
        />
        <Readiness
          bind:mental={parameters.readiness.mental}
          bind:physical={parameters.readiness.physical}
        />
        {#if gender === "female"}
          <MenstrualPain bind:menstrualPain={parameters.menstrualPain} />
        {/if}
        <Comment bind:comment={parameters.comments} />
        <button on:click={() => save(parameters)} type="button" class="button">
          {saved && JSON.stringify(parameters) === storedValues
            ? $_("generic.buttons.saved")
            : $_("generic.buttons.save")}
        </button>
      </div>
    </section>
  </div>
</div>
